import { useTranslations } from '@vocab/react';
import { Heading, Stack, Columns, Column, Box } from 'braid-design-system';
import React, { type Ref, type RefObject, useRef, useState } from 'react';
import { shallowEqual, useSelector } from 'react-redux';

import {
  SECTION_JOB_DESCRIPTION,
  SECTION_BRAND_SELECTION,
  SECTION_ABOUT_BUSINESS,
  SECTION_KEY_SELLING_POINTS,
  SECTION_CANDIDATE_SEARCH_RESULTS,
} from 'src/app/constants/pageSectionName';
import { PAGE_ID_WRITE } from 'src/app/constants/pageUid';
import {
  FEATURE_BRANDING,
  FEATURE_KEY_SELLING_POINTS,
  FEATURE_REORDER_WRITE_PAGE,
  useFeatureToggleData,
} from 'src/app/featureToggle';
import handleValidation from 'src/app/utils/handleValidation';
import { AboutBusiness } from 'src/app/views/components/AboutBusiness';
import { AdWritingToggler } from 'src/app/views/components/AdWritingToggler';
import { Branding } from 'src/app/views/components/Branding';
import { BrandingContextualHelp } from 'src/app/views/components/BrandingContextualHelp';
import Form from 'src/app/views/components/Form';
import { JobSummary } from 'src/app/views/components/JobSummary';
import { KeySellingPoints } from 'src/app/views/components/KeySellingPoints';
import { PageButtons } from 'src/app/views/components/PageButtons';
import { ProductFeatureBadge } from 'src/app/views/components/ProductFeatureBadge';
import { Video } from 'src/app/views/components/Video';
import { withDesktopEnhancers } from 'src/app/views/enhancers/withDesktopEnhancers';
import { useHandleEmailVerification } from 'src/app/views/hooks/useHandleEmailVerification';
import PageLayout from 'src/app/views/layouts/PageLayout';
import { PageSection } from 'src/app/views/sections/PageSection';
import type AppState from 'src/types/appState';
import type {
  GuidedWritingFlowRef,
  StandardWritingFlowRef,
} from 'src/types/formFields';
import type {
  CreateFlowPageContainer,
  CreateFlowPageProps,
} from 'src/types/pages';

import translations from './.vocab';

export const WritePage = (props: CreateFlowPageProps) => {
  const { t } = useTranslations(translations);
  const {
    handleDispatchDraft,
    handleNextPageRoute,
    handleProgrammaticRouteChange,
    handleSaveDraftFailed,
    pageErrorMessage,
  } = props;

  const { isGuided, isPrivateAdvertiser } = useSelector(
    (state: AppState) => ({
      isPrivateAdvertiser: state.session.hirerAccount?.isPrivateAdvertiser,
      isGuided: state.formData?.isGuided,
    }),
    shallowEqual,
  );
  const aboutBusinessRef: RefObject<any> = useRef();
  const guidedWritingFlowRef: Ref<GuidedWritingFlowRef> = useRef(null);
  const jobSummaryRef: RefObject<any> = useRef();
  const keySellingPointsRef: RefObject<any> = useRef();
  const standardWritingFlowRef: Ref<StandardWritingFlowRef> = useRef(null);

  const [isLoading, setIsLoading] = useState(false);

  const { handleEmailVerification } = useHandleEmailVerification();

  const containerComponentMapping: CreateFlowPageContainer[] = [
    {
      containerId: SECTION_ABOUT_BUSINESS,
      components: [aboutBusinessRef],
      validationCondition: () => isGuided,
    },
    {
      containerId: SECTION_JOB_DESCRIPTION,
      components: [guidedWritingFlowRef],
      validationCondition: () => isGuided,
    },
    {
      containerId: SECTION_JOB_DESCRIPTION,
      components: [standardWritingFlowRef],
      validationCondition: () => !isGuided,
    },
    {
      containerId: SECTION_CANDIDATE_SEARCH_RESULTS,
      components: [jobSummaryRef],
    },
    {
      containerId: SECTION_KEY_SELLING_POINTS,
      components: [keySellingPointsRef],
    },
  ];
  const handlePageValidation = async (skipIfNull = false) => {
    setIsLoading(true);
    await handleEmailVerification();
    const validationResult = handleValidation(
      containerComponentMapping,
      skipIfNull,
    );
    setIsLoading(false);
    return validationResult;
  };

  const isKeySellingPointsEnabled = useFeatureToggleData(
    FEATURE_KEY_SELLING_POINTS,
  );
  const isBrandingEnabled = useFeatureToggleData(FEATURE_BRANDING);

  const isReorderedLayoutEnabled = useFeatureToggleData(
    FEATURE_REORDER_WRITE_PAGE,
  );

  if (isReorderedLayoutEnabled) {
    return (
      <PageLayout
        hasAside
        hasProgressBar
        handleProgrammaticRouteChange={handleProgrammaticRouteChange}
        handleValidation={handlePageValidation}
        pageErrorMessage={pageErrorMessage}
        handleDispatchDraft={handleDispatchDraft}
        className="write-page-experiment"
      >
        <Stack space="medium">
          <Heading level="2">{t('write-page-heading')}</Heading>
          <Form
            handleNextPageRoute={handleNextPageRoute}
            handleValidation={handlePageValidation}
          >
            <Stack space="medium">
              <PageSection
                heading={t('write-page-job-description-section-heading')}
                sectionName={SECTION_JOB_DESCRIPTION}
                subHeading={t('write-page-job-description-section-subHeading')}
              >
                <Stack space="medium">
                  <Box
                    paddingBottom="medium"
                    data-testid="reordered-write-page"
                  >
                    <AdWritingToggler
                      standardWritingFlowRef={standardWritingFlowRef}
                      guidedWritingFlowRef={guidedWritingFlowRef}
                    />
                  </Box>
                </Stack>
                <Stack space="medium">
                  {isGuided && (
                    <Box>
                      <AboutBusiness ref={aboutBusinessRef} />
                    </Box>
                  )}
                  <Box>
                    <JobSummary ref={jobSummaryRef} />
                  </Box>
                  {isKeySellingPointsEnabled && (
                    <KeySellingPoints ref={keySellingPointsRef} />
                  )}
                </Stack>
              </PageSection>

              <PageSection sectionName={SECTION_BRAND_SELECTION}>
                {isBrandingEnabled && (
                  <Box paddingBottom="xlarge">
                    <Stack space="small">
                      <ProductFeatureBadge />
                      <Columns space="none">
                        <Column>
                          <Heading level="3">
                            {t('write-page-branding-section-heading')}
                          </Heading>
                        </Column>
                        {isPrivateAdvertiser ? null : (
                          <Column width="content">
                            <BrandingContextualHelp />
                          </Column>
                        )}
                      </Columns>
                      <Branding />
                    </Stack>
                  </Box>
                )}
                <Stack space="small">
                  <Box paddingTop="none">
                    <Video />
                  </Box>
                </Stack>
              </PageSection>

              <PageButtons
                isLoading={isLoading}
                handleSaveDraftFailed={handleSaveDraftFailed}
                handleValidation={handlePageValidation}
                showPreviewButton={true}
              />
            </Stack>
          </Form>
        </Stack>
      </PageLayout>
    );
  }

  return (
    <PageLayout
      hasAside
      hasProgressBar
      handleProgrammaticRouteChange={handleProgrammaticRouteChange}
      handleValidation={handlePageValidation}
      pageErrorMessage={pageErrorMessage}
      handleDispatchDraft={handleDispatchDraft}
    >
      <Stack space="medium">
        <Heading level="2">{t('write-page-heading')}</Heading>
        <Form
          handleNextPageRoute={handleNextPageRoute}
          handleValidation={handlePageValidation}
        >
          <Stack space="medium">
            {isBrandingEnabled && (
              <PageSection sectionName={SECTION_BRAND_SELECTION}>
                <Stack space="small">
                  <ProductFeatureBadge />
                  <Columns space="none">
                    <Column>
                      <Heading level="3">
                        {t('write-page-branding-section-heading')}
                      </Heading>
                    </Column>
                    {isPrivateAdvertiser ? null : (
                      <Column width="content">
                        <BrandingContextualHelp />
                      </Column>
                    )}
                  </Columns>
                  <Branding />
                </Stack>
              </PageSection>
            )}

            <PageSection
              heading={t('write-page-job-description-section-heading')}
              sectionName={SECTION_JOB_DESCRIPTION}
              subHeading={t('write-page-job-description-section-subHeading')}
            >
              <Stack space="medium">
                <Box paddingTop="xxsmall">
                  <AdWritingToggler
                    standardWritingFlowRef={standardWritingFlowRef}
                    guidedWritingFlowRef={guidedWritingFlowRef}
                  />
                </Box>
                <Video />
              </Stack>
            </PageSection>

            {isGuided && (
              <PageSection
                sectionName={SECTION_ABOUT_BUSINESS}
                intercomId="aboutBusinessSection"
              >
                <AboutBusiness ref={aboutBusinessRef} />
              </PageSection>
            )}

            <PageSection
              heading={t('write-page-search-results-section-heading')}
              sectionName={SECTION_CANDIDATE_SEARCH_RESULTS}
            >
              <Stack space="medium">
                <JobSummary ref={jobSummaryRef} />
                {isKeySellingPointsEnabled && (
                  <KeySellingPoints ref={keySellingPointsRef} />
                )}
              </Stack>
            </PageSection>

            <PageButtons
              isLoading={isLoading}
              handleSaveDraftFailed={handleSaveDraftFailed}
              handleValidation={handlePageValidation}
              showPreviewButton={true}
            />
          </Stack>
        </Form>
      </Stack>
    </PageLayout>
  );
};

export default withDesktopEnhancers(PAGE_ID_WRITE)(WritePage);
